import React, { useState } from 'react';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import { useRrpSettings } from '../../product/Products/productReportShared';
import { todayInTenant } from '../../../shared/tenantClock';
import DateInput from '../../../shared/DateInput';

const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { width: '100%' };

// Add/Edit form for a Pricelist 2.0 definition — Name + the same
// Group/Product Price Group/UoM scope Generate already collects, plus Valid
// From/To. Product scope is a Product Price Group (and its children) — a
// Customer's Customer Price Group is assigned per Product Price Group, so
// that's what makes a Pricelist's audience exact.
// Rounding lives on Price Rule, not here (see priceRule.js) — a Pricelist's
// scope can span many Rules at once, each with its own rounding, so there's
// no single figure to set at this level. No Status field here — that's a
// row action (Activate/Deactivate/Revert to Draft) on the list, not
// something set at save time.
function PricelistForm({ initial, customerPriceGroups, productPriceGroups, uoms, onSave, onCancel, loading }) {
  const [name, setName] = useState(initial?.name || '');
  const [customerPriceGroupId, setCustomerPriceGroupId] = useState(initial?.customer_price_group_id ?? '');
  const [productPriceGroupId, setProductPriceGroupId] = useState(initial?.product_price_group_id ?? '');
  const [uomId, setUomId] = useState(initial?.uom_id ?? '');
  const [validFrom, setValidFrom] = useState(initial?.valid_from || todayInTenant());
  const [validTo, setValidTo] = useState(initial?.valid_to || '');
  const [showRrp, setShowRrp] = useState(initial?.show_rrp ?? true);
  const [error, setError] = useState('');
  const { trackRrp } = useRrpSettings();

  const validate = () => {
    if (!name.trim()) return 'Name is required.';
    if (!customerPriceGroupId) return 'Customer Price Group is required.';
    if (!validFrom) return 'Valid From is required.';
    if (validTo && validTo < validFrom) return 'Valid To must be on or after Valid From.';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    onSave({
      name: name.trim(),
      customer_price_group_id: Number(customerPriceGroupId),
      product_price_group_id: productPriceGroupId ? Number(productPriceGroupId) : null,
      uom_id: uomId ? Number(uomId) : null,
      valid_from: validFrom,
      valid_to: validTo || null,
      show_rrp: showRrp,
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 240 }}>
          <label style={FIELD_LABEL_STYLE}>Name<RequiredMark /></label><br />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="e.g., Dealer Pricelist 2026"
            className="styled-input" style={FIELD_INPUT_STYLE}
          />
        </div>
        <div>
          <label style={FIELD_LABEL_STYLE}>Customer Price Group<RequiredMark /></label><br />
          <select className="styled-input" value={customerPriceGroupId} onChange={(e) => setCustomerPriceGroupId(e.target.value)} className="styled-input" style={FIELD_INPUT_STYLE}>
            <option value="">Select a group...</option>
            {customerPriceGroups.filter((g) => g.is_active).map((g) => (
              <option key={g.id} value={g.id}>{'   '.repeat(g.depth || 0)}{g.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={FIELD_LABEL_STYLE}>Product Price Group</label><br />
          <select className="styled-input" value={productPriceGroupId} onChange={(e) => setProductPriceGroupId(e.target.value)} className="styled-input" style={FIELD_INPUT_STYLE}>
            <option value="">All products</option>
            {productPriceGroups.map((c) => (
              <option key={c.id} value={c.id}>{'   '.repeat(c.depth || 0)}{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={FIELD_LABEL_STYLE}>UoM</label><br />
          <select className="styled-input" value={uomId} onChange={(e) => setUomId(e.target.value)} className="styled-input" style={FIELD_INPUT_STYLE}>
            <option value="">(every sellable UoM)</option>
            {uoms.map((u) => (
              <option key={u.id} value={u.id}>{u.code} — {u.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={FIELD_LABEL_STYLE}>Valid From<RequiredMark /></label><br />
          <DateInput value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="styled-input" style={FIELD_INPUT_STYLE} />
        </div>
        <div>
          <label style={FIELD_LABEL_STYLE}>Valid To</label><br />
          <DateInput value={validTo} onChange={(e) => setValidTo(e.target.value)} className="styled-input" style={FIELD_INPUT_STYLE} />
        </div>
      </div>

      {trackRrp && (
        <div style={{ marginTop: 14 }}>
          <ToggleSwitch
            checked={showRrp}
            onChange={setShowRrp}
            label="Show RRP (per base UoM, inc GST) on printed pricelist"
            title="Prints each Product's RRP per base UoM on the Compact and Extended Pricelists"
          />
        </div>
      )}

      {error && <div style={{ color: '#b91c1c', marginTop: 10, fontSize: 13 }}>{error}</div>}

      <div style={{ marginTop: 14 }}>
        <button type="submit" disabled={loading} style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} style={{ marginLeft: 8, padding: '8px 18px', background: 'none', color: '#5a6b85', border: '1px solid #bfc8d6', borderRadius: 6, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export default PricelistForm;
